import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cartService, addressService, orderService, couponService, siteConfigService } from '../../services/api';
import { ChevronLeft, CreditCard, Truck, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { updateMetaTags } from '../../utils/seo';

const Checkout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState(null);
  const [step, setStep] = useState(1); // 1: Address, 2: Payment, 3: Success
  const [paymentOptions, setPaymentOptions] = useState({ upi: true, card: true, cod: true });

  useEffect(() => {
    updateMetaTags({
      title: 'Checkout',
      description: 'Complete your purchase at Ortus FMCG.',
      siteName: 'Ortus FMCG'
    });

    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }
    loadCheckoutData();
    fetchPaymentSettings();
  }, [isAuthenticated]);

  const fetchPaymentSettings = async () => {
    try {
      const settings = await siteConfigService.settings();
      const options = {
        upi: settings?.payment_upi_enabled !== '0',
        card: settings?.payment_card_enabled !== '0',
        cod: settings?.payment_cod_enabled !== '0',
      };
      setPaymentOptions(options);
      const available = Object.entries(options).find(([, enabled]) => enabled);
      if (available) {
        setPaymentMethod(available[0]);
      }
    } catch (error) {
      console.warn('Payment settings missing, falling back to defaults');
    }
  };

  const loadCheckoutData = async () => {
    try {
      setLoading(true);
      const [cartData, addressData] = await Promise.all([
        cartService.index(),
        addressService.index()
      ]);
      setCart(cartData);
      const allAddresses = addressData || [];
      setAddresses(allAddresses);

      const shipping = allAddresses.filter((addr) => addr.type === 'shipping');
      const billing = allAddresses.filter((addr) => addr.type === 'billing');
      setShippingAddresses(shipping);
      setBillingAddresses(billing);

      const defaultShipping = shipping.find((addr) => addr.is_default) || shipping[0] || null;
      const defaultBilling = billing.find((addr) => addr.is_default) || billing[0] || null;
      setSelectedShippingAddress(defaultShipping?.id || null);
      setSelectedBillingAddress(defaultBilling?.id || null);
    } catch (error) {
      console.error('Error loading checkout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const result = await couponService.validate(couponCode, cart?.subtotal || 0);
      setDiscount(Number(result.discount || 0));
      setMessage({ type: 'success', text: `Coupon applied! You saved ₹${result.discount}` });
    } catch (error) {
      setDiscount(0);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Invalid coupon code.' });
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedShippingAddress) {
      setMessage({ type: 'error', text: 'Please select a shipping address.' });
      return;
    }

    if (!billingSameAsShipping && !selectedBillingAddress) {
      setMessage({ type: 'error', text: 'Please select a billing address or keep it same as shipping.' });
      return;
    }

    try {
      setSubmitting(true);
      await orderService.checkout({
        shipping_address_id: selectedShippingAddress,
        billing_address_id: billingSameAsShipping ? null : selectedBillingAddress,
        payment_method: paymentMethod,
        coupon_code: couponCode || undefined,
      });
      
      setStep(3); // Success
      window.scrollTo(0, 0);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Checkout failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
    </div>
  );

  if (step === 3) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-red-100">
        <CheckCircle2 size={40} />
      </div>
      <h1 className="text-4xl font-black text-slate-900 mb-4">Order Placed Successfully!</h1>
      <p className="text-slate-500 mb-10 text-lg">
        Thank you for your purchase. We've sent a confirmation email to <span className="text-slate-900 font-bold">{user?.email}</span>.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button 
          onClick={() => navigate('/profile')}
          className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
        >
          View My Orders
        </button>
        <button 
          onClick={() => navigate('/shop')}
          className="px-8 py-4 bg-white text-slate-900 border-2 border-slate-100 rounded-2xl font-bold hover:border-red-600 hover:text-red-600 transition-all"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );

  const cartTotal = Math.max(0, Number(cart?.total || 0) - discount);
  const shippingSummary = shippingAddresses.find((addr) => addr.id === selectedShippingAddress) || null;
  const billingSummary = billingAddresses.find((addr) => addr.id === selectedBillingAddress) || null;

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4">
        <button 
          onClick={() => navigate('/shop')}
          className="mb-8 flex items-center gap-2 text-slate-500 hover:text-red-600 transition-colors font-bold"
        >
          <ChevronLeft size={20} /> Back to Shop
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Checkout Flow */}
          <div className="lg:col-span-2 space-y-8">
            {/* Step 1: Shipping Address */}
            <div className={`bg-white rounded-3xl p-8 shadow-sm border-2 transition-all ${step === 1 ? 'border-red-600 ring-4 ring-red-50' : 'border-white'}`}>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${step === 1 ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600'}`}>1</div>
                  <h2 className="text-2xl font-black text-slate-900">Shipping Address</h2>
                </div>
                {step > 1 && (
                  <button onClick={() => setStep(1)} className="text-red-600 font-bold text-sm hover:underline">Edit</button>
                )}
              </div>

              {step === 1 && (
                <div className="space-y-8">
                  <div>
                    {shippingAddresses.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {shippingAddresses.map((addr) => (
                          <div
                            key={addr.id}
                            onClick={() => setSelectedShippingAddress(addr.id)}
                            className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                              selectedShippingAddress === addr.id
                                ? 'border-red-600 bg-red-50'
                                : 'border-slate-100 hover:border-slate-200 bg-white'
                            }`}
                          >
                            <div className="font-bold text-slate-900 mb-1">{addr.full_name}</div>
                            <div className="text-sm text-slate-500 leading-relaxed mb-3">
                              {(addr.address_line1 || addr.address_line_1)}, {addr.city}
                              <br />
                              {addr.state}, {addr.postal_code}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{addr.type}</div>
                              {addr.is_default && (
                                <span className="text-[10px] uppercase tracking-[0.3em] text-red-600 font-black">Default</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <p className="text-slate-500 mb-4">No shipping addresses saved yet.</p>
                        <button
                          onClick={() => navigate('/profile')}
                          className="text-red-600 font-bold hover:underline"
                        >
                          Add a Shipping Address in Profile
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 flex items-center gap-3 text-sm text-slate-600">
                    <input
                      id="billingToggle"
                      type="checkbox"
                      checked={billingSameAsShipping}
                      onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                      className="w-5 h-5 text-red-600 rounded"
                    />
                    <label htmlFor="billingToggle" className="font-semibold">
                      Billing address same as shipping
                    </label>
                  </div>

                  {!billingSameAsShipping && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-slate-900">Billing Address</h3>
                        <Link to="/profile" className="text-xs font-semibold text-red-600">Manage</Link>
                      </div>
                      {billingAddresses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {billingAddresses.map((addr) => (
                            <div
                              key={addr.id}
                              onClick={() => setSelectedBillingAddress(addr.id)}
                              className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                                selectedBillingAddress === addr.id
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-slate-100 hover:border-slate-200 bg-white'
                              }`}
                            >
                              <div className="font-bold text-slate-900 mb-1">{addr.full_name}</div>
                              <div className="text-sm text-slate-500 leading-relaxed mb-3">
                                {(addr.address_line1 || addr.address_line_1)}, {addr.city}
                                <br />
                                {addr.state}, {addr.postal_code}
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{addr.type}</div>
                                {addr.is_default && (
                                  <span className="text-[10px] uppercase tracking-[0.3em] text-red-600 font-black">Default</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                          <p className="text-slate-500 mb-4">No billing addresses yet.</p>
                          <button
                            onClick={() => navigate('/profile')}
                            className="text-red-600 font-bold hover:underline"
                          >
                            Add a Billing Address in Profile
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 pt-2">
                    <Link to="/profile" className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:border-red-500">
                      Manage Addresses
                    </Link>
                  </div>

                  <button
                    disabled={!selectedShippingAddress || (!billingSameAsShipping && !selectedBillingAddress)}
                    onClick={() => setStep(2)}
                    className="w-full sm:w-auto px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 disabled:opacity-50 transition-all shadow-xl shadow-slate-200"
                  >
                    Continue to Payment
                  </button>
                </div>
              )}

              {step > 1 && selectedShippingAddress && (
                <div className="space-y-1 text-slate-600 text-sm">
                  {shippingSummary && (
                    <p>
                      <span className="font-semibold text-slate-900">Shipping:</span>{' '}
                      {shippingSummary.full_name}, {(shippingSummary.address_line1 || shippingSummary.address_line_1)}, {shippingSummary.city}
                    </p>
                  )}
                  {billingSameAsShipping ? (
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Billing same as shipping</p>
                  ) : (
                    billingSummary && (
                      <p>
                        <span className="font-semibold text-slate-900">Billing:</span>{' '}
                        {billingSummary.full_name}, {(billingSummary.address_line1 || billingSummary.address_line_1)}, {billingSummary.city}
                      </p>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Payment Method */}
            <div className={`bg-white rounded-3xl p-8 shadow-sm border-2 transition-all ${step === 2 ? 'border-red-600 ring-4 ring-red-50' : 'border-white'}`}>
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${step === 2 ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600'}`}>2</div>
                <h2 className="text-2xl font-black text-slate-900">Payment Method</h2>
              </div>

              {step === 2 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { id: 'upi', name: 'UPI / QR Code', icon: CreditCard },
                      { id: 'card', name: 'Credit / Debit Card', icon: CreditCard },
                      { id: 'cod', name: 'Cash on Delivery', icon: Truck },
                    ].filter((method) => paymentOptions[method.id]).map((method) => (
                      <div 
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`p-6 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${
                          paymentMethod === method.id 
                          ? 'border-red-600 bg-red-50 text-red-700' 
                          : 'border-slate-100 hover:border-slate-200 bg-white text-slate-600'
                        }`}
                      >
                        <method.icon size={24} />
                        <span className="font-bold">{method.name}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                    <div className="flex items-center gap-3 text-red-600 mb-2">
                      <ShieldCheck size={20} />
                      <span className="font-bold text-sm uppercase tracking-wider">Secure Payment</span>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Your transaction is encrypted and secure. By placing an order, you agree to our terms and conditions.
                    </p>
                  </div>

                  <button 
                    disabled={submitting}
                    onClick={handlePlaceOrder}
                    className="w-full sm:w-auto px-10 py-4 bg-red-600 text-white rounded-2xl font-black text-lg hover:bg-red-700 disabled:opacity-50 transition-all shadow-xl shadow-red-100 flex items-center justify-center gap-3"
                  >
                    {submitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Place Order & Pay ₹{cartTotal}</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <h3 className="text-xl font-black text-slate-900 mb-6">Order Summary</h3>
              
              <div className="space-y-4 mb-8">
                {cart?.items?.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-slate-500">
                      {item.product?.name}
                      {item.variant && (
                        <span className="block text-xs text-purple-600 font-bold">{item.variant.name}</span>
                      )}
                      <span className="text-slate-400 font-bold"> x{item.quantity}</span>
                    </span>
                    <span className="font-bold text-slate-900">₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-100">
                <div className="flex justify-between text-slate-500 text-sm">
                  <span>Subtotal</span>
                  <span className="font-bold text-slate-900">₹{cart?.subtotal || 0}</span>
                </div>
                <div className="flex justify-between text-slate-500 text-sm">
                  <span>GST (13%)</span>
                  <span className="font-bold text-slate-900">₹{cart?.tax || 0}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-red-600 text-sm">
                    <span>Discount</span>
                    <span className="font-bold">-₹{discount}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl pt-4 border-t border-slate-100">
                  <span className="font-black text-slate-900">Total</span>
                  <span className="font-black text-red-600">₹{cartTotal}</span>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    placeholder="Coupon Code" 
                    className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button 
                    onClick={applyCoupon}
                    className="bg-slate-900 text-white px-4 rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors"
                  >
                    Apply
                  </button>
                </div>
                {message && (
                  <div className={`text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-2 ${
                    message.type === 'success' ? 'bg-red-50 text-red-600' : 'bg-rose-50 text-rose-600'
                  }`}>
                    <AlertCircle size={14} />
                    {message.text}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3 justify-center text-slate-400">
              <ShieldCheck size={18} />
              <span className="text-xs font-bold uppercase tracking-widest">100% Satisfaction Guaranteed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
